import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { consumeReturnUrl, defaultDashboardFor } from "@/lib/postLoginRedirect";

// Self-hosted Google OAuth callback. The browser arrives here as
//   /auth/callback?code=...&state=...
// after Google redirects back from accounts.google.com.
// We POST the code to our backend (which has the client_secret) — it returns our JWT.
export default function AuthCallback() {
  const { exchangeGoogleCode } = useAuth();
  const nav = useNavigate();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const googleErr = params.get("error");

    if (googleErr) {
      setErr(`Google: ${googleErr}`);
      toast.error("Google sign-in cancelled");
      setTimeout(() => nav("/login", { replace: true }), 1500);
      return;
    }
    if (!code) {
      setErr("Missing authorization code");
      toast.error("Google login failed — no code received");
      setTimeout(() => nav("/login", { replace: true }), 1500);
      return;
    }
    // CSRF check
    const expectedState = sessionStorage.getItem("g_oauth_state");
    if (expectedState && state !== expectedState) {
      setErr("Security check failed (state mismatch)");
      toast.error("Security check failed — please try again");
      setTimeout(() => nav("/login", { replace: true }), 1500);
      return;
    }
    sessionStorage.removeItem("g_oauth_state");

    (async () => {
      try {
        const redirectUri = window.location.origin + "/auth/callback";
        const user = await exchangeGoogleCode(code, redirectUri, state);
        toast.success(`Welcome ${user.name}!`);
        const dest = consumeReturnUrl() || defaultDashboardFor(user.role);
        nav(dest, { replace: true });
      } catch (e) {
        const msg = e.response?.data?.detail || "Google login failed";
        setErr(msg);
        toast.error(msg);
        setTimeout(() => nav("/login", { replace: true }), 1500);
      }
    })();
  }, [exchangeGoogleCode, nav]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <div className="glass-strong rounded-3xl p-8" data-testid="auth-callback">
        <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-600 animate-pulse" />
        <div className="mt-5 font-display text-xl font-semibold">
          {err ? "Sign-in interrupted" : "Signing you in…"}
        </div>
        <div className="text-sm text-[var(--text-mute)] mt-2">
          {err || "Google account verify ho raha hai. Ek second…"}
        </div>
      </div>
    </div>
  );
}
